import React, { useState, useEffect, useCallback } from 'react';
import {
  Gift, DollarSign, TrendingUp, Calendar, Upload, CheckCircle,
  Clock, XCircle, ChevronRight, AlertCircle, FileText, X, Camera
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeMatchDashboard = ({ primaryColor = '#3B82F6', onClose }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExternalModal, setShowExternalModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [externalForm, setExternalForm] = useState({
    charity_name: '',
    charity_ein: '',
    donation_amount: '',
    donation_date: new Date().toISOString().split('T')[0],
    receipt_image: '',
    external_platform: ''
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/matching/employee-dashboard`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setExternalForm(prev => ({ ...prev, receipt_image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const submitExternalDonation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/matching/external-donation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...externalForm,
          donation_amount: parseFloat(externalForm.donation_amount)
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage({ type: 'success', text: data.message });
        setShowExternalModal(false);
        setExternalForm({
          charity_name: '',
          charity_ein: '',
          donation_amount: '',
          donation_date: new Date().toISOString().split('T')[0],
          receipt_image: '',
          external_platform: ''
        });
        fetchDashboard();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to submit' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to submit external donation' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: primaryColor }} />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-3" />
        <p className="text-gray-600">Unable to load matching dashboard</p>
      </div>
    );
  }

  const budgetPercentage = dashboard.budget_percentage_used || 0;

  return (
    <div className="bg-white rounded-xl shadow-lg max-w-4xl mx-auto">
      {/* Header */}
      <div className="p-6 border-b" style={{ background: `linear-gradient(135deg, ${primaryColor}15, ${primaryColor}05)` }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: primaryColor }}>
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Your Matching Gift Dashboard</h2>
              <p className="text-sm text-gray-500">Track your donation matches and remaining budget</p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mx-6 mt-4 p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Budget Overview */}
      <div className="p-6">
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-500">Annual Budget</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: primaryColor }}>
              ${dashboard.annual_budget?.toLocaleString()}
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-gray-500">Used This Year</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">
              ${dashboard.annual_used?.toLocaleString()}
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-4 h-4 text-green-500" />
              <span className="text-xs text-gray-500">Remaining</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              ${dashboard.remaining_budget?.toLocaleString()}
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-gray-500">Lifetime Matched</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">
              ${dashboard.lifetime_match_received?.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Budget Usage</span>
            <span className="text-sm" style={{ color: primaryColor }}>{budgetPercentage.toFixed(1)}% used</span>
          </div>
          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(budgetPercentage, 100)}%`,
                background: budgetPercentage > 90 ? '#EF4444' : budgetPercentage > 70 ? '#F59E0B' : primaryColor
              }}
            />
          </div>
        </div>

        {/* External Donations Button */}
        {dashboard.matching_settings?.external_donations_enabled && (
          <div className="mb-6 p-4 bg-purple-50 rounded-xl border border-purple-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Upload className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="font-semibold text-gray-900">Made a donation outside of work?</p>
                  <p className="text-sm text-gray-600">Upload your receipt and we&apos;ll match it!</p>
                </div>
              </div>
              <button
                onClick={() => setShowExternalModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> Submit External Donation
              </button>
            </div>
          </div>
        )}

        {/* Pending Requests */}
        {dashboard.pending_requests_count > 0 && (
          <div className="mb-6 p-4 bg-orange-50 rounded-xl border border-orange-100">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-orange-800">
                You have {dashboard.pending_requests_count} pending match request(s)
              </span>
            </div>
          </div>
        )}

        {/* Match History */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-4">Recent Match History</h3>
          {dashboard.match_history?.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-xl">
              <Gift className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No matched donations yet</p>
              <p className="text-sm text-gray-400">Start donating to see your matches here!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboard.match_history?.slice(0, 5).map((donation, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {donation.metadata?.charity_name || 'Charity Donation'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(donation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">${donation.amount}</p>
                    <p className="text-sm text-green-600">+${donation.employer_match_amount} matched</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* External Requests */}
        {dashboard.external_match_requests?.length > 0 && (
          <div className="mt-6">
            <h3 className="font-semibold text-gray-900 mb-4">External Donation Requests</h3>
            <div className="space-y-3">
              {dashboard.external_match_requests.map((request, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      request.status === 'pending' ? 'bg-orange-100' :
                      request.status === 'approved' || request.status === 'completed' ? 'bg-green-100' :
                      request.status === 'vesting' ? 'bg-blue-100' : 'bg-red-100'
                    }`}>
                      {request.status === 'pending' ? <Clock className="w-5 h-5 text-orange-600" /> :
                       request.status === 'approved' || request.status === 'completed' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                       request.status === 'vesting' ? <Clock className="w-5 h-5 text-blue-600" /> :
                       <XCircle className="w-5 h-5 text-red-600" />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{request.charity_name}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(request.created_at).toLocaleDateString()} • {request.external_platform || 'Direct'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">${request.donation_amount}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      request.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                      request.status === 'approved' || request.status === 'completed' ? 'bg-green-100 text-green-700' :
                      request.status === 'vesting' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {request.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* External Donation Modal */}
      {showExternalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Submit External Donation</h3>
                <button onClick={() => setShowExternalModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <form onSubmit={submitExternalDonation} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charity Name *</label>
                <input
                  type="text"
                  required
                  value={externalForm.charity_name}
                  onChange={(e) => setExternalForm(prev => ({ ...prev, charity_name: e.target.value }))}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., American Red Cross"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Donation Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      required
                      min="1"
                      value={externalForm.donation_amount}
                      onChange={(e) => setExternalForm(prev => ({ ...prev, donation_amount: e.target.value }))}
                      className="w-full pl-8 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="100"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Donation Date *</label>
                  <input
                    type="date"
                    required
                    value={externalForm.donation_date}
                    onChange={(e) => setExternalForm(prev => ({ ...prev, donation_date: e.target.value }))}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charity EIN (Optional)</label>
                <input
                  type="text"
                  value={externalForm.charity_ein}
                  onChange={(e) => setExternalForm(prev => ({ ...prev, charity_ein: e.target.value }))}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="XX-XXXXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Where did you donate?</label>
                <select
                  value={externalForm.external_platform}
                  onChange={(e) => setExternalForm(prev => ({ ...prev, external_platform: e.target.value }))}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select platform...</option>
                  <option value="Direct">Direct to charity</option>
                  <option value="GoFundMe">GoFundMe</option>
                  <option value="Facebook">Facebook Fundraiser</option>
                  <option value="PayPal Giving">PayPal Giving Fund</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Receipt *</label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  {externalForm.receipt_image ? (
                    <div>
                      <img
                        src={externalForm.receipt_image}
                        alt="Receipt"
                        className="max-h-40 mx-auto mb-2 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => setExternalForm(prev => ({ ...prev, receipt_image: '' }))}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Camera className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">Click to upload receipt image</p>
                      <p className="text-xs text-gray-400">JPG, PNG up to 5MB</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {dashboard.remaining_budget > 0 && externalForm.donation_amount && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">
                    <strong>Estimated Match:</strong> ${
                      Math.min(
                        parseFloat(externalForm.donation_amount || 0) * (dashboard.matching_settings?.default_ratio || 1),
                        dashboard.remaining_budget
                      ).toLocaleString()
                    }
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowExternalModal(false)}
                  className="flex-1 px-4 py-3 border rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !externalForm.receipt_image}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit for Matching'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeMatchDashboard;
